// Configuration des contraintes pour la caméra
const constraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000,
    channelCount: 1,
    // Optimisations spécifiques pour Safari iOS
    googEchoCancellation: true,
    googAutoGainControl: true,
    googNoiseSuppression: true,
    googHighpassFilter: true,
    googTypingNoiseDetection: true,
  },
  video: {
    width: { min: 1280, ideal: 1920 },
    height: { min: 720, ideal: 1080 },
    facingMode: { ideal: "environment" },
    frameRate: { min: 25, ideal: 30, max: 30 },
  },
};

// Fonction pour démarrer la caméra
const startCamera = async (cameraConstraints, callback) => {
  // Validation des contraintes
  if (!cameraConstraints || typeof cameraConstraints !== 'object') {
    console.error("Contraintes invalides, utilisation des contraintes par défaut");
    cameraConstraints = constraints;
  }

  console.log("Démarrage de la caméra avec contraintes:", cameraConstraints);

  let stream;
  try {
    stream = await getStream(cameraConstraints);
  } catch (error) {
    console.error("Erreur lors du démarrage de la caméra :", error);
    callback({ success: false, error });
    return;
  }
  window.stream = stream;
  callback({ success: true, stream });
};

const getStream = async (cameraConstraints) => {
  try {
    return await navigator.mediaDevices.getUserMedia(cameraConstraints);
  } catch (error) {
    if (error.name !== "OverconstrainedError") throw error;
    // Cameras below the HD minimums: take what the device offers.
    return navigator.mediaDevices.getUserMedia({
      ...cameraConstraints,
      video: { facingMode: cameraConstraints.video.facingMode },
    });
  }
};

const cameraErrorMessage = (error) => {
  switch (error && error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "L'accès à la caméra a été refusé. Autorisez la caméra et le micro pour ce site dans les réglages de votre navigateur, puis réessayez.";
    case "NotReadableError":
    case "AbortError":
      return "Votre caméra est déjà utilisée par une autre application ou un autre onglet. Fermez-les, puis réessayez.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Aucune caméra compatible n'a été trouvée sur cet appareil.";
    case "TypeError":
      return "Ce navigateur ne permet pas d'accéder à la caméra. Ouvrez le lien dans Chrome ou Safari, puis réessayez.";
    default:
      return "Impossible de démarrer la caméra. Réessayez, ou utilisez un autre appareil.";
  }
};

// Fonction pour arrêter la caméra
const stopCamera = (callback) => {
  if (window.stream) {
    window.stream.getTracks().forEach((track) => track.stop());
    window.stream = null;
    callback({ success: true });
  } else {
    callback({
      success: false,
      error: "Aucun flux actif trouvé pour arrêter la caméra.",
    });
  }
};

const FRAME_TIMEOUT_MS = 1000;

const play = async (video) => {
  try {
    await video.play();
  } catch (error) {
    // nextFrame decides: a refused play() just times out there.
  }
};

// A (re)loaded element presents the track's last frame once, even from a
// source that emits nothing more (measured in Chrome): wait for the next one.
const nextFrame = (video) =>
  new Promise((resolve) => {
    if (!video.requestVideoFrameCallback) return resolve(true);
    const timer = setTimeout(() => resolve(false), FRAME_TIMEOUT_MS);
    video.requestVideoFrameCallback(() =>
      video.requestVideoFrameCallback(() => {
        clearTimeout(timer);
        resolve(true);
      })
    );
  });

// A paused preview keeps its last frame while the call track runs on:
// a photo is drawn only after a newer frame.
const waitForLiveFrame = async (video, stream) => {
  await play(video);
  if (await nextFrame(video)) return true;
  if (stream) video.srcObject = stream;
  await play(video);
  return nextFrame(video);
};

// Fonction pour activer ou désactiver le son
const updateAudio = (audioMuted) => {
  if (window.stream) {
    const audioTrack = window.stream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = audioMuted;
      return { success: true };
    }
  }
  return { success: false, error: "Aucune piste audio active trouvée." };
};

// Fonction pour activer ou désactiver la vidéo
const updateVideo = (cameraDisabled) => {
  if (window.stream) {
    const videoTrack = window.stream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = cameraDisabled;
      return { success: true };
    }
  }
  return { success: false, error: "Aucune piste vidéo active trouvée." };
};

// Fonction pour inverser la caméra entre avant et arrière
const reverseCamera = (constraints, callback) => {
  stopCamera((result) => {
    if (result.success) {
      // Inversion de la caméra (avant <-> arrière)
      constraints.video.facingMode.ideal =
        constraints.video.facingMode.ideal === "environment"
          ? "user"
          : "environment";

      callback(constraints);
    } else {
      callback({ success: false, error: "Impossible d'inverser la caméra." });
    }
  });
};

// Export des fonctions
export {
  constraints,
  startCamera,
  cameraErrorMessage,
  stopCamera,
  updateAudio,
  updateVideo,
  reverseCamera,
  waitForLiveFrame,
};
